import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { LayoutTypes } from "./LayoutBuilder";
import {
    TextField,
    FormLabel,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@material-ui/core";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
    type Row,
    SortingState,
} from "@tanstack/react-table";
import NumericalTextField from "../material-components/Misc/NumericalTextField";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { updateAttribute } from "../../actions/flowActions";
import {
    getFlowExternalServiceForSelectedFlowItem,
    getFlowItemExternalServiceDefeaultFields,
    getClientFieldAliasForSelectedFlowItem,
} from "../../reducers/flowExternalServices";
import { getUEPersonLayoutFieldData } from "../../helpers/flowControlHelper";
import { getFlowExternalServiceParametersForSelectedFlowItem } from "../../reducers/flowExternalServiceParameters";
import { getItemParameterValue } from "../../helpers/flowControlHelper";

// Types
import { IExternalServiceLayoutField, IExternalServiceLayout } from "../../types/stores/externalServiceLayoutData";
import { IAppState } from "../../types/stores";
import { IEnrichmentField } from "../../types/stores/umbrellaData";
import { getExportOutputLayoutColumns } from "../../actions/flowControlActions";
import { qualifiers } from "../../helpers/constants";

interface IProps {
    layoutId: number;
    isSnowflakeTable?: boolean;
    hasColumnOptFeats?: boolean;
    showColumnOptService?: boolean;
}

const colHelper = createColumnHelper<IExternalServiceLayoutField>();

const LayoutPreview: React.FC<IProps> = ({
    layoutId,
    isSnowflakeTable,
    hasColumnOptFeats,
    showColumnOptService,
}: IProps) => {
    const dispatch = useDispatch();

    const [layout, setLayout] = useState<IExternalServiceLayout | undefined>(undefined);
    const [layoutFieldsPreview, setLayoutFieldsPreview] = useState<Array<IExternalServiceLayoutField>>([]);

    const [uePersonFields, setUEPersonFields] = useState<Array<IEnrichmentField>>([]);
    const [localDelimiter, setLocalDelimiter] = useState("");
    const [selectedQualifier, setSelectedQualifier] = useState("");
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<SortingState>([]);

    const layouts = useSelector((state: IAppState) => state.externalServiceLayoutData.externalServiceLayouts);
    const allLayoutFields = useSelector(
        (state: IAppState) => state.externalServiceLayoutData.externalServiceLayoutFields
    );
    const serviceFields = useSelector((state: IAppState) => state.externalServiceLayoutData.externalServiceFields);
    const flowExternalService = useSelector((state: IAppState) => getFlowExternalServiceForSelectedFlowItem(state));
    const availableEnrichmentFields = useSelector((state: IAppState) => state.umbrellaData.currEnrichmentFields);
    const fileDelimiters = useSelector((state: IAppState) => state.session.fileDelimiters);
    const exportLockedOut = useSelector((state: IAppState) => state.externalServiceLayoutData.isLockedOut);

    const showOutputColumnOpt = hasColumnOptFeats && isSnowflakeTable && showColumnOptService;

    const itemServiceDefaultFields = useSelector((state: IAppState) => getFlowItemExternalServiceDefeaultFields(state));
    const flowExternalServiceParameters = useSelector((state: IAppState) =>
        getFlowExternalServiceParametersForSelectedFlowItem(state)
    );
    const listExternalParameters = useSelector((state: IAppState) => state.flowControlData.externalParameters);
    const itemClientFieldAlias = useSelector((state: IAppState) => getClientFieldAliasForSelectedFlowItem(state));

    //getting Field Name Type value
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures || []);
    const altUEFieldName =
        enabledFeatures.includes("flow-control-field-name-type") &&
        getItemParameterValue(listExternalParameters, flowExternalServiceParameters, "output_field_names") == "true";

    // Features
    const fcQualifiersFeature = enabledFeatures.includes("flow-control-qualifiers");

    useLayoutEffect(() => {
        setLayout(layouts.find(l => l.LayoutId == layoutId));
        const currFields = [...allLayoutFields.filter(x => x.LayoutId == layoutId)];
        setFinalLayoutFields(currFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutId, layouts, allLayoutFields]);

    const loadAllUEFields = useCallback((fields: Array<IEnrichmentField>) => {
        const personFields = getUEPersonLayoutFieldData(fields);
        setUEPersonFields(personFields);
    }, []);

    useEffect(() => {
        if (layout && layout.LayoutType == LayoutTypes.Output && availableEnrichmentFields.length > 0) {
            loadAllUEFields(availableEnrichmentFields);
        }
    }, [loadAllUEFields, layout, availableEnrichmentFields]);

    // Set LocalDelimiter
    useEffect(() => {
        if (layout) {
            if (layout.RelativePositions && layout.Delimiter != null && layout.Delimiter.length > 0) {
                if (fileDelimiters.filter(x => x.Delimiter == layout.Delimiter).length > 0) {
                    setLocalDelimiter(layout.Delimiter);
                } else {
                    setLocalDelimiter("custom");
                }
            }
        }
    }, [layout, fileDelimiters]);

    // Set SelectedQualifier
    useEffect(() => {
        if (layout?.RelativePositions) {
            setSelectedQualifier(layout.Qualifier ?? "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout?.RelativePositions]);

    const findClientAlias = (serviceFieldId: number, textName: string): string | null => {
        let alias: string | null = null;
        if (serviceFieldId > 0) {
            const standard = itemClientFieldAlias.filter(x => x.ServiceFieldId == serviceFieldId);
            if (standard.length == 1) {
                alias = standard[0].FieldAlias;
            }
        }
        if (textName.trim().length > 0) {
            const custom = itemClientFieldAlias.filter(x => x.FieldName == textName.trim());
            if (custom.length == 1) {
                alias = custom[0].FieldAlias;
            }
        }
        return alias;
    };

    const setFinalLayoutFields = (layoutFields: Array<IExternalServiceLayoutField>) => {
        if (enabledFeatures.includes("flow-control-custom-headers")) {
            layoutFields.forEach(x => {
                x.FieldAlias = findClientAlias(x.ServiceFieldId, x.FieldName ?? "");
            });
        }
        setLayoutFieldsPreview(layoutFields);
    };

    const renderFieldName = (row: IExternalServiceLayoutField) => {
        let fieldName = "Field not found.";
        if (row.ServiceFieldId > 0) {
            const field = serviceFields.find(f => f.ServiceFieldId == row.ServiceFieldId);
            if (field) {
                fieldName = field.DisplayName;
            }
        } else {
            fieldName = row.FieldName!;
            let fieldShortName = row.ShortName;
            const itemDefaultFields = itemServiceDefaultFields.map(x => x.FieldName);
            if (!isNaN(parseInt(fieldName || ""))) {
                let name =
                    altUEFieldName && !itemDefaultFields.includes(fieldName || "")
                        ? uePersonFields.find(x => x.FieldId == parseInt(fieldName || ""))?.AlternateName ||
                          availableEnrichmentFields.find(y => y.FieldId == parseInt(fieldName || ""))?.AlternateName ||
                          ""
                        : uePersonFields.find(x => x.FieldId == parseInt(fieldName || ""))?.FieldName ||
                          availableEnrichmentFields.find(y => y.FieldId == parseInt(fieldName || ""))?.FieldName ||
                          "";
                let shortName =
                    uePersonFields.find(x => x.FieldId == parseInt(fieldShortName || ""))?.FieldName ||
                    uePersonFields.find(y => y.FieldId == parseInt(fieldShortName || ""))?.FieldName ||
                    availableEnrichmentFields.find(y => y.FieldId == parseInt(fieldShortName || ""))?.FieldName ||
                    "";
                fieldName = name != "" ? name : shortName != "" ? "Table Name " + shortName : row.FieldName!;
            }
        }
        return fieldName;
    };

    const globalFilterFn = (row: Row<IExternalServiceLayoutField>, _columnId: string, filterValue: string) => {
        const term = filterValue.toLowerCase();
        const r = row.original;
        const haystack = [renderFieldName(r), r.FieldAlias ?? "", r.FieldPosition, r.FieldLength, r.EncryptionType]
            .join(" ")
            .toLowerCase();
        return haystack.indexOf(term) >= 0;
    };

    const tableColumns = useMemo(
        () => [
            colHelper.display({
                id: "FieldName",
                header: "Field Name",
                cell: ({ row }) => renderFieldName(row.original),
            }),
            colHelper.accessor("FieldAlias", {
                header: "Alias",
                cell: ({ getValue }) => getValue() ?? "",
            }),
            colHelper.accessor("FieldPosition", {
                header: layout?.RelativePositions ? "Column Number" : "Position",
            }),
            colHelper.accessor("FieldLength", { header: "Length" }),
            colHelper.accessor("EncryptionType", { header: "Encryption" }),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout?.RelativePositions, serviceFields, uePersonFields, availableEnrichmentFields]
    );

    const columnVisibility = {
        FieldAlias:
            !!layout?.RelativePositions &&
            layout?.LayoutType !== LayoutTypes.Input &&
            enabledFeatures.includes("flow-control-custom-headers"),
    };

    const table = useReactTable({
        data: layoutFieldsPreview,
        columns: tableColumns,
        state: { pagination, globalFilter, columnVisibility, sorting },
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const renderDelimiterOption = () => {
        const options = fileDelimiters.map(x => (
            <MenuItem key={x.DelimiterId} value={x.Delimiter}>
                {x.DelimiterName}
            </MenuItem>
        ));
        options.push(
            <MenuItem key={999} value="custom">
                Custom Delimiter
            </MenuItem>
        );

        return (
            <FormControl>
                <InputLabel id="delimiter-select-label">Delimiter</InputLabel>
                <Select id="delimiter-select" value={localDelimiter} style={{ width: "175px" }} readOnly={true}>
                    {options}
                </Select>
            </FormControl>
        );
    };

    const renderQualifierOption = () => {
        const options = qualifiers?.map((qualifier, index) => (
            <MenuItem key={index} value={qualifier.value}>
                {qualifier.label}
            </MenuItem>
        ));

        return (
            <FormControl>
                <InputLabel id="quanlifier-label" shrink>
                    Field Qualifier - {localDelimiter}VALUE{localDelimiter}
                </InputLabel>
                <Select
                    id="field-qualifier"
                    required
                    displayEmpty
                    value={selectedQualifier}
                    onChange={e => setSelectedQualifier(e.target.value as string)}
                    disabled={!layout?.RelativePositions}
                    style={{ width: "175px" }}
                    readOnly={true}
                >
                    {options}
                </Select>
            </FormControl>
        );
    };

    const exportOutputLayoutColumns = () =>
        dispatch(
            getExportOutputLayoutColumns(
                flowExternalService.OutputColumnNameOpt,
                flowExternalService.OutputLayoutId,
                altUEFieldName,
                flowExternalService.WFClientCode
            )
        );

    return layout ? (
        <>
            <div>
                <FormLabel>Layout Preview</FormLabel>
                <div style={{ display: "flex", padding: "10px" }}>
                    <TextField
                        id="layout-type"
                        label="Layout Type"
                        value={layout.LayoutType == LayoutTypes.Input ? "Input" : "Output"}
                        InputProps={{
                            readOnly: true,
                        }}
                        style={{ width: "90px", marginRight: "25px" }}
                    />
                    {layout.RelativePositions ? (
                        <>
                            <div style={{ margin: "0 25px 0 0" }}>
                                {renderDelimiterOption()}
                                {localDelimiter == "custom" && (
                                    <TextField
                                        id="delimiter-char"
                                        label="Custom"
                                        type="text"
                                        value={layout.Delimiter || ""}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        style={{ width: "100px", marginLeft: "25px" }}
                                    />
                                )}
                            </div>
                            {fcQualifiersFeature ? renderQualifierOption() : null}
                        </>
                    ) : (
                        <NumericalTextField
                            id="record-size"
                            label="Record Size"
                            value={layout.RecordSize}
                            InputProps={{
                                readOnly: true,
                            }}
                            style={{ width: "90px" }}
                        />
                    )}
                    {showOutputColumnOpt && (
                        <div>
                            <FormControl
                                style={{
                                    flexGrow: 2,
                                    fontSize: 18,
                                    margin: "0 0 0 25px",
                                    minWidth: 250,
                                }}
                            >
                                <InputLabel htmlFor={`deploy-setting`}>Output Column Name Option</InputLabel>
                                <Select
                                    id="deploy-setting"
                                    style={{ width: "65%" }}
                                    value={flowExternalService.OutputColumnNameOpt || 0}
                                    onChange={e =>
                                        dispatch(
                                            updateAttribute(
                                                "flowExternalServices",
                                                flowExternalService.FlowExternalServiceId,
                                                "OutputColumnNameOpt",
                                                e.target.value,
                                                true
                                            )
                                        )
                                    }
                                >
                                    <MenuItem key={"outputOpt0"} value={0}>
                                        Field Name
                                    </MenuItem>
                                    <MenuItem key={"outputOpt1"} value={1}>
                                        {"F{Field Id}"}
                                    </MenuItem>
                                    <MenuItem key={"outputOpt2"} value={2}>
                                        {"F{Field Id}_{Field Name}"}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                id="export-output-columns"
                                variant="contained"
                                color="secondary"
                                startIcon={
                                    <i
                                        className="material-icons"
                                        style={{ height: "18px", width: "15px", fontSize: "15px" }}
                                    >
                                        storage
                                    </i>
                                }
                                onClick={() => exportOutputLayoutColumns()}
                                disabled={exportLockedOut}
                            >
                                Export Output Column Format
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 0",
                    }}
                >
                    <span style={{ fontSize: 16 }}>Layout Fields</span>
                    <TextField
                        id="layout-preview-search"
                        placeholder="Search"
                        value={globalFilter}
                        onChange={e => setGlobalFilter(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <i className="material-icons" style={{ fontSize: 18, marginRight: 4 }}>
                                    search
                                </i>
                            ),
                        }}
                    />
                </div>
                <TableContainer>
                    <Table>
                        <TableHead>
                            {table.getHeaderGroups().map(hg => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map(header => {
                                        const sortable = header.column.getCanSort();
                                        return (
                                            <TableCell key={header.id} style={{ fontSize: 12, zIndex: 0 }}>
                                                {sortable ? (
                                                    <TableSortLabel
                                                        active={!!header.column.getIsSorted()}
                                                        direction={
                                                            header.column.getIsSorted() === "desc" ? "desc" : "asc"
                                                        }
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </TableSortLabel>
                                                ) : (
                                                    flexRender(header.column.columnDef.header, header.getContext())
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody>
                            {table.getRowModel().rows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={table.getVisibleLeafColumns().length}
                                        style={{ textAlign: "center", padding: "20px", fontSize: 12 }}
                                    >
                                        No records to display
                                    </TableCell>
                                </TableRow>
                            ) : (
                                table.getRowModel().rows.map(row => (
                                    <TableRow key={row.id} hover>
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell key={cell.id} style={{ fontSize: 12, padding: "10px 8px" }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            flexWrap: "nowrap",
                            padding: "6px 16px",
                            fontSize: 12,
                        }}
                    >
                        <Select
                            variant="standard"
                            disableUnderline
                            value={pagination.pageSize}
                            onChange={e => table.setPageSize(Number(e.target.value))}
                            renderValue={(v: unknown) => <span style={{ fontSize: 12 }}>{`${v} rows`}</span>}
                            style={{ fontSize: 12, marginRight: 8, flexShrink: 0 }}
                        >
                            {[10, 25, 50].map(size => (
                                <MenuItem key={size} value={size} style={{ fontSize: 12 }}>
                                    {size}
                                </MenuItem>
                            ))}
                        </Select>
                        <span style={{ margin: "0 8px", whiteSpace: "nowrap", flexShrink: 0 }}>
                            {table.getFilteredRowModel().rows.length === 0
                                ? 0
                                : pagination.pageIndex * pagination.pageSize + 1}
                            -
                            {Math.min(
                                table.getFilteredRowModel().rows.length,
                                (pagination.pageIndex + 1) * pagination.pageSize
                            )}{" "}
                            of {table.getFilteredRowModel().rows.length}
                        </span>
                        <IconButton
                            size="small"
                            disabled={!table.getCanPreviousPage()}
                            onClick={() => table.setPageIndex(0)}
                        >
                            <i className="material-icons" style={{ fontSize: 18 }}>
                                first_page
                            </i>
                        </IconButton>
                        <IconButton
                            size="small"
                            disabled={!table.getCanPreviousPage()}
                            onClick={() => table.previousPage()}
                        >
                            <i className="material-icons" style={{ fontSize: 18 }}>
                                navigate_before
                            </i>
                        </IconButton>
                        <IconButton size="small" disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>
                            <i className="material-icons" style={{ fontSize: 18 }}>
                                navigate_next
                            </i>
                        </IconButton>
                        <IconButton
                            size="small"
                            disabled={!table.getCanNextPage()}
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        >
                            <i className="material-icons" style={{ fontSize: 18 }}>
                                last_page
                            </i>
                        </IconButton>
                    </div>
                </TableContainer>
            </div>
        </>
    ) : null;
};

export default LayoutPreview;
